import { useSyncExternalStore } from "react";

import { LANGUAGES, type Locale } from "./languages";
import { pickLocale } from "./localeMatch";
export { LANGUAGES, type Locale } from "./languages";
export type Messages = Record<string, string>;
export const LOCALE_STORAGE_KEY = "ato.pwa.locale";
const catalogs: Record<Exclude<Locale, "en">, () => Promise<Messages>> = {
  ja: () => import("./locales/ja.json").then((module) => module.default),
  "zh-CN": () => import("./locales/zh-CN.json").then((module) => module.default),
  "zh-TW": () => import("./locales/zh-TW.json").then((module) => module.default),
  ko: () => import("./locales/ko.json").then((module) => module.default),
  es: () => import("./locales/es.json").then((module) => module.default),
  fr: () => import("./locales/fr.json").then((module) => module.default),
  de: () => import("./locales/de.json").then((module) => module.default),
  "pt-BR": () => import("./locales/pt-BR.json").then((module) => module.default),
};
const loaded = new Map<Locale, Messages>();
const listeners = new Set<() => void>();
let locale: Locale = "en";
let request = 0;

const LOCALE_CODES: readonly string[] = LANGUAGES.map((language) => language.code);

export function isLocale(value: unknown): value is Locale {
  return LANGUAGES.some((language) => language.code === value);
}
export function getLocale(): Locale {
  return locale;
}
function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}
export function useLocale(): Locale {
  return useSyncExternalStore(subscribe, getLocale, () => "en");
}

/** Load before committing: a failed/older request cannot change the chosen language. */
export async function setLocale(next: Locale, persist = true): Promise<void> {
  if (!isLocale(next)) throw new Error("Unsupported language");
  const ticket = ++request;
  if (next !== "en" && !loaded.has(next)) {
    const load = catalogs[next];
    if (!load) throw new Error("Language unavailable");
    loaded.set(next, await load());
  }
  if (ticket !== request) return;
  locale = next;
  if (typeof document !== "undefined") {
    document.documentElement.lang = next;
    document.documentElement.dir = "ltr";
  }
  if (persist && typeof window !== "undefined") {
    try { window.localStorage.setItem(LOCALE_STORAGE_KEY, next); } catch {
      // Storage may be disabled; the selection still applies for this visit.
    }
  }
  listeners.forEach((listener) => listener());
}

/** Maps browser language tags to a supported locale: exact tag, Chinese script/region, then base language. */
export function matchLocale(tags: readonly string[]): Locale | null {
  return pickLocale(null, tags, LOCALE_CODES) as Locale | null;
}

function browserLocale(): Locale {
  if (typeof navigator === "undefined") return "en";
  const tags = navigator.languages?.length ? navigator.languages : [navigator.language];
  return matchLocale(tags.filter(Boolean)) ?? "en";
}

/** A saved choice wins; otherwise follow the browser's languages, falling back to English. */
export async function initializeLocale(): Promise<void> {
  let saved: string | null = null;
  try { saved = window.localStorage.getItem(LOCALE_STORAGE_KEY); } catch { /* Use the browser language. */ }
  try { await setLocale(isLocale(saved) ? saved : browserLocale(), false); }
  catch { await setLocale("en", false); }
}

export function messageText(message: string): string {
  const catalog = loaded.get(locale);
  const translated = catalog && Object.prototype.hasOwnProperty.call(catalog, message) ? catalog[message] : undefined;
  return typeof translated === "string" && translated ? translated : message;
}
export function t(message: string | null | undefined, values: Record<string, unknown> = {}): string {
  return messageText(message ?? "").replace(/\{\{(\w+)\}\}/g, (token, key: string) =>
    Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : token,
  );
}

export function plural(count: number, one: string, other: string, values: Record<string, unknown> = {}): string {
  return t(new Intl.PluralRules(locale).select(count) === "one" ? one : other, values);
}
